import React from 'react'
import { Link } from 'react-router-dom'
import Seo from './Seo'

const FAQ_ITEMS = [
    {
        question: 'Where are the Advanced Analytics?',
        answer: (
            <p>
                Champion-specific advanced analytics live on each champion&apos;s Stats page under{' '}
                <Link className="font-semibold text-cyan-300 underline decoration-cyan-300/40 underline-offset-4 transition-colors hover:text-cyan-200" to="/analytics/champions">
                    Champion Analytics
                </Link>
                . That is where you can find scaling profiles, best evolution order, Red Kayn vs Blue Kayn form comparisons, first item and rush item timing, jungle and
                support item completion times, builds, matchups, and synergies. Open a completed match from a player&apos;s Match History to find
                True Lead—a gold-equivalent view of how your team is actually performing over time.
            </p>
        ),
    },
    {
        question: 'Why use WINRATES.GG?',
        answer: (
            <p>
                WINRATES.GG brings the work players often split across several apps into one convenient place: player profiles, champion and item statistics, ranked tier lists, and a beautiful desktop and mobile UI for player performance. WINRATES.GG takes it a step further with critical information that free companion apps often leave out. WINRATES.GG includes evolution-based champion statistics ("What is the best evolution order?"), correct Kayn-form statistics (Blue Kayn vs Red Kayn), champion-mastery-based performance statistics, legendary
                item completion times, jungle and support item completion times, and estimated champion 'over' and 'under' performance
                estimates. You can actually learn the degree to which a champion is performant, and how they skew along ELO & mastery bands. The short version is: WINRATES.GG is the unified free companion app.
            </p>
        ),
    },
    {
        question: 'When will other regions/servers be available?',
        answer: (
            <p>
                The WINRATES.GG beta is scoped to NA (Americas) because I (vanilli vanilli#vv2) am an NA-based player most familiar
                with the NA scene, culture, and dataset. NA hosts a substantial share of all League of Legends® games, and the
                plan is to support every League server as the product grows and scaling and hosting costs stabilize.
            </p>
        ),
    },
    {
        question: 'How does the WINRATES.GG score work?',
        answer: (
            <p>
                Other apps may rate the same game differently because WINRATES.GG's performance score focuses on who helped create
                game-winning advantages, not just who finished with the best weighted stats. That context matters. Playing from
                ahead is a skill, but it is not terribly impressive. On the flip side, having a lead while your entire team is
                losing is impressive. If you frag out or take objectives in a dire game state, you deserve more credit. It is
                possible to win games with low contribution and lose games with high contribution. Either way, you can always
                play better, and you can always expect continuous improvements from WINRATES.GG.
            </p>
        ),
    },
    {
        question: 'How can I contact you or leave a donation?',
        answer: (
            <p>
                Visit the{' '}
                <Link className="font-semibold text-cyan-300 underline decoration-cyan-300/40 underline-offset-4 transition-colors hover:text-cyan-200" to="/contact">
                    contact page
                </Link>{' '}
                to send a message, report a bug, or leave a donation. Your support helps cover the hosting and scaling costs
                behind expanding WINRATES.GG beyond NA.
            </p>
        ),
    },
    {
        question: 'Why am I seeing rate limits and retry countdowns?',
        answer: (
            <p>
                WINRATES.GG was approved through Riot's third-party developer process and is connected to the live League of Legends® services. You
                can read Riot's{' '}
                <a
                    className="font-semibold text-cyan-300 underline decoration-cyan-300/40 underline-offset-4 transition-colors hover:text-cyan-200"
                    href="https://www.riotgames.com/en/legal"
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    Legal Jibber Jabber
                </a>{' '}
                here. The application still has a shared traffic allowance, though. If WINRATES.GG generates more requests than Riot
                has currently allocated to it, Riot applies a rate limit and asks us to wait before trying again. Riot can see where
                the traffic comes from, and if usage stays high, WINRATES.GG may qualify for more throughput, which means shorter
                cooldowns, more data, more fast(er) :). In the meantime, the countdown is the app respecting Riot's limit rather
                than blindly sending their servers any traffic.
            </p>
        ),
    },
    {
        question: 'Why does a live-game lookup sometimes fail?',
        answer: (
            <p>
                Riot's live spectator service can occasionally return a transient network error even when the game and the player
                are fine. That happens on Riot's side, so WINRATES.GG cannot prevent every occurrence. We handle it with retries and clear
                feedback, and a later attempt may work normally.
            </p>
        ),
    },
]

const FAQ = () => (
    <div className="mx-auto w-full max-w-screen-lg px-4 pt-6 pb-4 text-zinc-200 custom-lg:px-0 sm:pt-8">
        <Seo
            title="WINRATES.GG FAQ | Unified League of Legends Companion App"
            description="Find WINRATES.GG advanced champion analytics, True Lead, player profiles, match history, and answers about region availability and player contribution scores."
        />
        <section aria-labelledby="faq-heading" className="w-full max-w-[820px]">
            <header className="mb-6">
                <h1 id="faq-heading" className="text-2xl font-semibold tracking-tight text-zinc-100 sm:text-3xl">
                    FAQ
                </h1>
            </header>

            <div className="space-y-3">
                {FAQ_ITEMS.map(({ question, answer }) => (
                    <article key={question} className="rounded-xl border border-zinc-800/90 bg-zinc-950/60 px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]">
                        <h2 className="text-sm font-semibold text-zinc-200">{question}</h2>
                        <div className="mt-2 text-sm leading-6 text-zinc-400">{answer}</div>
                    </article>
                ))}
            </div>
        </section>
    </div>
)

export default FAQ
